import {
  ApplicationConfig,
  Injector,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';

import { appRoutes } from '@/app.routes';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { createCustomElement } from '@angular/elements';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { ApiModule, BASE_PATH } from '@eemo/api-client';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ScriptLoaderService } from 'angular-google-charts';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { CookieService } from 'ngx-cookie-service';
import { getFrenchPaginatorIntl } from '../app/extensions/frenchPaginatorIntl';
import { ErrorInterceptor } from '../app/handlers/error.interceptor';
import { LoaderInterceptor } from '../app/handlers/loader.interceptor';
import { getApiBaseUrl } from '../app/helpers/app.constants';
import { provideEemoIcons } from '../app/modules/icon-eemo-module/icon-eemo.providers';
import { environment } from '../environments/environment';
import { PropertyCardComponent } from './components/property-card/property-card.component';
import { VersionCheckService } from './services/version-check.service';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  // add the haash in query string to avoid cache issues
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?' + VersionCheckService.codeHash);
}

export const MY_FORMAT: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

registerLocaleData(localeFr, 'fr');

export function registerCustomElements(injector: Injector): void {
  const PropertyCardElement = createCustomElement(PropertyCardComponent, {
    injector,
  });
  customElements.define('property-card-element', PropertyCardElement);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withComponentInputBinding()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'fr',
      }),
      ApiModule,
      RecaptchaV3Module,
    ),
    provideEemoIcons(),
    // adaptateur de dates natif (avant MAT_DATE_FORMATS ci-dessous, qui le surcharge)
    provideNativeDateAdapter(),
    // service unique de chargement de Google Charts, utilisé par les graphiques de suivi
    ScriptLoaderService,
    { provide: BASE_PATH, useValue: getApiBaseUrl() },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: MatPaginatorIntl, useValue: getFrenchPaginatorIntl() },
    { provide: LOCALE_ID, useValue: 'fr' },
    CookieService,
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideAppInitializer(() => registerCustomElements(inject(Injector))),
  ],
};
